import { onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import pkceChallenge from 'pkce-challenge';
import { useAuthStore } from '@/stores/authStore';
import { ROUTES } from '@myorg/type-lib';
import { jwtDecode } from 'jwt-decode';
import { logger } from '@myorg/type-lib';
import { STORAGE_KEYS } from '@/config/storageKeys';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://app.bloomingrice.com';
const DEEP_LINK_SCHEME = 'bloomingriceapp';
const OAUTH_CALLBACK_URL = `${DEEP_LINK_SCHEME}://oauth2/callback`;
const APP_CLIENT_ID = 'app-client';

export function useAuth() {
  const router = useRouter();
  
  // 1. 觸發登入：喚起系統瀏覽器
  const loginWithOAuth2 = async () => {
    try {
      // 生成 PKCE code_verifier 和 code_challenge
      const { code_verifier, code_challenge } = await pkceChallenge();
      
      // 將 code_verifier 存入 Preferences，後續換取 Token 時需要用到
      await Preferences.set({ key: 'code_verifier', value: code_verifier });
      
      // 構建 OAuth2 授權網址
      const authUrl = `${API_BASE_URL}/api/server/oauth2/authorize` +
        `?client_id=${APP_CLIENT_ID}` +
        `&response_type=code` +
        `&code_challenge=${code_challenge}` +
        `&code_challenge_method=S256` +
        `&redirect_uri=${encodeURIComponent(OAUTH_CALLBACK_URL)}`;

      logger.log('OAuth2 授權網址:', authUrl);
      
      // 喚起原生系統瀏覽器（iOS 會自動使用 ASWebAuthenticationSession，Android 使用 Custom Tabs）
      await Browser.open({ url: authUrl, windowName: '_blank' });
    } catch (error) {
      logger.error('啟動 OAuth2 登入失敗:', error);
      throw error;
    }
  };

  // 2. 處理 Deep Link 回呼的函數
  const handleDeepLink = async (data: { url: string }) => {
    const url = data.url;
    
    logger.log('收到 Deep Link:', url);
    
    // 確認是不是我們要的 Callback 網址
    if (url.startsWith(OAUTH_CALLBACK_URL)) {
      // 關閉系統瀏覽器視窗
      await Browser.close();

      // 解析 URL 中的 code 與 state
      const parsedUrl = new URL(url);
      const code = parsedUrl.searchParams.get('code');
      const state = parsedUrl.searchParams.get('state');

      if (code) {
        logger.log('成功拿到授權碼 (Code):', code);
        
        // 🚀 接下來：呼叫您的後端 API 換取 Token
        await exchangeCodeForToken(code);
      } else {
        logger.error('未收到授權碼');
      }
    }
  };

  // 3. 向認證伺服器換取 Token (由 APP 後台發送 POST)
  const exchangeCodeForToken = async (code: string) => {
    try {
      // 從 Preferences 取回之前儲存的 code_verifier
      const { value: codeVerifier } = await Preferences.get({ key: 'code_verifier' });
      
      if (!codeVerifier) {
        throw new Error('找不到 code_verifier，無法進行 PKCE 驗證');
      }

      // 注意：此處直接向 Auth Server 發送請求，需帶上步驟一對應的明文 code_verifier
      const response = await axios.post(`${API_BASE_URL}/api/server/oauth2/token`, 
        new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: APP_CLIENT_ID,
          code: code,
          redirect_uri: OAUTH_CALLBACK_URL,
          code_verifier: codeVerifier
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }
      );

      const tokens = response.data;
      logger.log('成功取得 Tokens:', tokens);

      // 💾 安全儲存 Token
      // 建議使用 @capacitor-community/secure-storage 存入 Keychain/SharedPreferences
      // 這裡先使用 Preferences，正式上線時應改用 secure storage
      await Preferences.set({ key: 'access_token', value: tokens.access_token });
      await Preferences.set({ key: 'refresh_token', value: tokens.refresh_token });
      
      // 清除 code_verifier，不再需要
      await Preferences.remove({ key: 'code_verifier' });

      logger.log('Token 儲存成功');

      // 解碼 JWT token 取得使用者資訊（現在包含 userId 和 merchantId）
      const decodedToken: any = jwtDecode(tokens.access_token);
      logger.log('解碼後的 Token:', decodedToken);

      // 直接設定 auth store 狀態，不需要呼叫 API
      const authStore = useAuthStore();
      const userId = decodedToken.userId || decodedToken.sub || '';
      const merchantId = decodedToken.merchantId || '';
      const name = decodedToken.username || decodedToken.name || decodedToken.sub || '';
      const roles = decodedToken.roles || [];

      logger.log('[useAuth] Storing user info in localStorage:');
      logger.log('[useAuth] userId:', userId);
      logger.log('[useAuth] merchantId:', merchantId);
      logger.log('[useAuth] username:', name);
      logger.log('[useAuth] roles:', roles);

      // 防禦 3：確保有值才塞進 localStorage，避免塞入 "undefined" 字串
      if (userId) localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
      if (merchantId) localStorage.setItem(STORAGE_KEYS.MERCHANT_ID, merchantId);
      if (name) localStorage.setItem(STORAGE_KEYS.USERNAME, name);
      localStorage.setItem(STORAGE_KEYS.USER_ROLES, JSON.stringify(roles));
      authStore.setLoginStatus(true);

      logger.log('[useAuth] localStorage after storing:');
      logger.log('[useAuth] localStorage.getItem("userId"):', localStorage.getItem(STORAGE_KEYS.USER_ID));
      logger.log('[useAuth] localStorage.getItem("merchantId"):', localStorage.getItem(STORAGE_KEYS.MERCHANT_ID));
      logger.log('[useAuth] localStorage.getItem("username"):', localStorage.getItem(STORAGE_KEYS.USERNAME));
      logger.log('[useAuth] localStorage.getItem("userRoles"):', localStorage.getItem(STORAGE_KEYS.USER_ROLES));

      // 使用 router.push 避免頁面重新載入，保留 auth store 狀態
      router.push(ROUTES.USER.PATH);
    } catch (error) {
      logger.error('換取 Token 失敗:', error);
      throw error;
    }
  };

  // 4. 刷新 Token
  const refreshToken = async (): Promise<string | null> => {
    try {
      const { value: refreshToken } = await Preferences.get({ key: 'refresh_token' });
      
      if (!refreshToken) {
        throw new Error('沒有 refresh_token');
      }

      const response = await axios.post(`${API_BASE_URL}/api/server/oauth2/token`,
        new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
          client_id: APP_CLIENT_ID
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }
      );

      const tokens = response.data;
      
      // 更新儲存的 Token
      await Preferences.set({ key: 'access_token', value: tokens.access_token });
      if (tokens.refresh_token) {
        await Preferences.set({ key: 'refresh_token', value: tokens.refresh_token });
      }

      return tokens.access_token;
    } catch (error) {
      logger.error('刷新 Token 失敗:', error);
      // 刷新失敗，清除所有 Token
      await logout();
      return null;
    }
  };

  // 5. 取得當前的 Access Token
  const getAccessToken = async (): Promise<string | null> => {
    const { value: accessToken } = await Preferences.get({ key: 'access_token' });
    return accessToken;
  };

  // 6. 登出
  const logout = async () => {
    logger.log('[logout] Starting logout process');
    try {
      const { value: refreshToken } = await Preferences.get({ key: 'refresh_token' });
      const { value: accessToken } = await Preferences.get({ key: 'access_token' });

      logger.log('[logout] Retrieved tokens - accessToken:', !!accessToken, 'refreshToken:', !!refreshToken);

      // Native App 模式：執行完整的登出流程（token revoke + session invalidate）
      if (Capacitor.isNativePlatform()) {
        logger.log('[logout] Native App mode detected');

        // 1. 呼叫 OAuth Server 的 revoke endpoint 撤銷 refresh token
        if (refreshToken) {
          try {
            logger.log('[logout] Calling OAuth Server revoke endpoint');
            await axios.post(`${API_BASE_URL}/api/server/oauth2/revoke`,
              new URLSearchParams({
                token: refreshToken,
                client_id: APP_CLIENT_ID
              }),
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded'
                }
              }
            );
            logger.log('[logout] Refresh token revoked successfully');
          } catch (revokeError) {
            logger.error('[logout] Failed to revoke refresh token:', revokeError);
            // 繼續執行後續清理，不中斷登出流程
          }
        } else {
          logger.log('[logout] No refresh token found, skipping revoke');
        }

        // 2. 呼叫 OAuth Client 的 logout endpoint 撤銷 session
        if (accessToken) {
          try {
            logger.log('[logout] Calling OAuth Client logout endpoint');
            await axios.post(`${API_BASE_URL}/api/client/logout`,
            {},
            {
              headers: {
                'Authorization': `Bearer ${accessToken}`
              }
            });
            logger.log('[logout] Server session invalidated successfully');
          } catch (sessionError) {
            logger.error('[logout] Failed to invalidate server session:', sessionError);
            // 繼續執行後續清理，不中斷登出流程
          }
        } else {
          logger.log('[logout] No access token found, skipping session invalidate');
        }
      } else {
        logger.log('[logout] Web mode detected, redirecting to logout endpoint');
        // 網頁版：導向登出端點（由 server 處理 session 清理）
        window.location.href = '/api/client/logout';
        return;
      }

      // 3. 清除本地儲存的 Token
      logger.log('[logout] Clearing Preferences');
      await Preferences.remove({ key: 'access_token' });
      await Preferences.remove({ key: 'refresh_token' });
      await Preferences.remove({ key: 'code_verifier' });

      // 4. 清除 localStorage 中的使用者資訊
      logger.log('[logout] Clearing localStorage');
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
      localStorage.removeItem(STORAGE_KEYS.MERCHANT_ID);
      localStorage.removeItem(STORAGE_KEYS.USERNAME);
      localStorage.removeItem(STORAGE_KEYS.USER_ROLES);

      logger.log('[logout] Local storage cleared successfully');
      logger.log('[logout] Logout process completed');
    } catch (error) {
      logger.error('[logout] 登出失敗:', error);
    }
  };

  // 7. 檢查是否已登入
  const isAuthenticated = async (): Promise<boolean> => {
    const accessToken = await getAccessToken();
    return !!accessToken;
  };

  // 8. 生命週期鉤子：註冊與銷毀 APP 監聽器
  const setupDeepLinkListener = () => {
    onMounted(() => {
      App.addListener('appUrlOpen', handleDeepLink);
    });

    onUnmounted(() => {
      App.removeAllListeners();
    });
  };

  return {
    loginWithOAuth2,
    refreshToken,
    getAccessToken,
    logout,
    isAuthenticated,
    setupDeepLinkListener
  };
}
